/* The note strip between the pattern band and the canvas.
 *
 * It borrows the overflow banner's geometry (`PlanBuilderScreen.module.css`) — a full-width row with
 * the canvas's own side padding and a hairline under it — and none of its colour: the overflow
 * banner reports a data problem and wears the warn tint, while this states a fact about the plan and
 * is neutral. No accent: nothing here is an action (the accent is a scalpel), and the dismiss is a
 * plain ✕.
 *
 * `flex: none`, like every other row above the canvas, so the canvas keeps the space it had. */

.note {
  display: flex;
  flex: none;
  gap: var(--space-3);
  align-items: flex-start;
  padding: var(--space-2) var(--space-20px);
  background: var(--bg-chip);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* Nothing to say. The box goes and the region stays — `display: none` would take the region out of
 * the accessibility tree, which is indistinguishable from never having rendered it and is the
 * defect this row exists to answer. The same pair as `.overflow` / `.overflowEmpty` one file over. */
.noteEmpty {
  padding: var(--space-0);
  background: none;
  border-bottom: 0;
}

.message {
  flex: 1;
  margin: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* The ✕, drawn as the ghost every other dismissal in this screen is: ink and nothing else, and a
 * focus ring that is the app's own. */
.dismiss {
  position: relative;
  flex: none;
  padding: var(--space-0) var(--space-2px);
  font-family: var(--font-body);
  font-size: var(--fs-note);
  line-height: var(--lh-none);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: 0;
}

/* The glyph is 12 x 13 painted, which is half of WCAG 2.2 SC 2.5.8's floor. The invisible target
 * grows to 26 x 27 and the drawing does not — `--hit-extend-lg` is the token that exists for a mark
 * this small, and the search field's own clear button uses it for the same reason. */
.dismiss::after {
  position: absolute;
  inset: var(--hit-extend-lg);
  content: "";
}

.dismiss:hover {
  color: var(--text);
}

.dismiss:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-sm);
}
